import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

const translations = {
  en: {
    // Navigation
    nav: {
      programs: 'Programs',
      howItWorks: 'How does it work?',
      difference: 'The OFA Difference',
      returningFamilies: 'Stay Bilingual',
      becomeBilingual: 'Become Bilingual',
      diploma: 'Diploma & Pathways',
      admissions: 'Admissions',
      tuition: 'Tuition',
      about: 'About',
      partnerships: 'Partnerships',
      bookCall: 'Register',
      requestInfo: 'Request Info',
    },
    // Hero Section
    hero: {
      overline: '1ST FRENCH-AMERICAN ONLINE SCHOOL',
      title: 'OFA France',
      titleHighlight: 'An American education.',
      titleHighlight2: 'A bilingual future.',
      titleHighlight3: 'For everyone.',
      subtitle: 'Alongside their French schooling, your child follows an accredited American program — from Kindergarten to 12th grade — and earns a U.S. High School Diploma.',
      cta: 'Register',
      secondaryCta: 'Explore Programs',
      proof1: '4-8 Students',
      proof1Sub: 'per class',
      proof2: 'COGNIA',
      proof2Sub: 'Accredited',
      proof3: 'K-12',
      proof3Sub: 'Uninterrupted American pathway',
      proof4: '100% Online',
      proof4Sub: 'Live classes from home',
    },
    // Problem Section
    problem: {
      overline: 'THE CHALLENGE',
      title: 'How to give an international dimension to your child\'s education?',
      subtitle: 'In an increasingly global world, English proficiency and international academic credentials are essential for future success.',
      point1Title: 'Limited English Exposure',
      point1Desc: 'French schools offer limited hours of English instruction, insufficient for true academic bilingualism.',
      point2Title: 'Expat Children Lose Ground',
      point2Desc: 'Children returning from abroad often lose their English proficiency within months of returning to France.',
      point3Title: 'International Schools Are Inaccessible',
      point3Desc: 'With waiting lists and high costs, international schools remain out of reach for most families.',
    },
    // Solution Section
    solution: {
      overline: 'THE SOLUTION',
      title: 'A Structured Academic Pathway in English',
      subtitle: 'OFA France provides a complete American curriculum alongside your child\'s French education, building genuine bilingual academic competency.',
      feature1: 'Not a Language School',
      feature1Desc: 'We teach academic subjects in English, not just the language itself.',
      feature2: 'Long-Term Coherence',
      feature2Desc: 'A structured K-12 pathway, not isolated tutoring sessions.',
      feature3: 'Dual System Expertise',
      feature3Desc: 'Designed specifically for students navigating both French and American academic expectations.',
    },
    // Pathway Section
    pathway: {
      overline: 'THE ACADEMIC JOURNEY',
      title: 'From Kindergarten to High School Diploma',
      subtitle: 'A complete educational trajectory building toward international opportunities.',
      k5Title: 'K-5 Elementary',
      k5Desc: 'Build early bilingual foundations through engaging English instruction.',
      middleTitle: 'Middle School',
      middleDesc: 'Structured academic English with writing, literature, and critical thinking.',
      highTitle: 'High School',
      highDesc: 'Advanced coursework with optional US High School Diploma preparation.',
    },
    // Outcomes Section
    outcomes: {
      overline: 'FUTURE OPPORTUNITIES',
      title: 'Opening Doors Worldwide',
      subtitle: 'OFA France graduates are prepared for top universities in France, Europe, the UK, and North America.',
      stat1: '100%',
      stat1Label: 'University Acceptance',
      stat2: '15+',
      stat2Label: 'Countries Represented',
      stat3: '90%',
      stat3Label: 'Report Career Benefits',
    },
    // Proof Section
    proof: {
      overline: 'WHY CHOOSE US',
      title: 'The OFA France Advantage',
      small: 'Small Classes',
      smallDesc: '4-8 students maximum ensures personalized attention and meaningful participation.',
      teachers: 'Expert Teachers',
      teachersDesc: 'Native English-speaking educators with experience in international curricula.',
      accredited: 'COGNIA Accredited',
      accreditedDesc: 'Our US High School Diploma is recognized by universities worldwide.',
      dual: 'Dual-System Design',
      dualDesc: 'Built specifically for students balancing French and American academics.',
    },
    // Objection Section
    objection: {
      overline: 'COMMON QUESTIONS',
      title: 'Understanding the Fees',
      q1: 'How is this different from tutoring?',
      a1: 'OFA France is a structured academic program, not tutoring. We follow a complete American curriculum with qualified teachers, regular assessments, and a long-term educational trajectory.',
      q2: 'How does it compare to international schools?',
      a2: 'We offer the academic rigor of international schools at a fraction of the cost, with the flexibility to maintain your child\'s French schooling and social connections.',
      q3: 'Will my child be overwhelmed?',
      a3: 'Our program is designed to complement French schooling with just 2x45 minutes per week, plus flexible asynchronous work. Most students find it enriching, not burdensome.',
    },
    // CTA Section
    cta: {
      title: 'Ready to Build Your Child\'s International Future?',
      subtitle: 'Schedule a free discovery call with our admissions team to discuss your family\'s goals.',
      primary: 'Register',
      secondary: 'Request Information',
    },
    // Footer
    footer: {
      tagline: 'Building international academic futures from France.',
      programs: 'Programs',
      about: 'About',
      contact: 'Contact',
      legal: 'Legal',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      copyright: '© 2025 OFA France. All rights reserved.',
      partOf: 'Part of the OFA Lycée network.',
    },
    // Programs Page
    programs: {
      heroTitle: 'Academic Programs',
      heroSubtitle: 'A complete K-12 pathway designed for students in France seeking genuine bilingual education.',
      k5: {
        title: 'Elementary (K-5)',
        subtitle: 'Building the Foundation',
        desc: 'Our elementary program establishes strong English language foundations through engaging, age-appropriate instruction that develops reading, writing, and speaking skills.',
        features: ['Interactive storytelling and reading', 'Creative writing workshops', 'Vocabulary building through play', 'Cultural exploration activities'],
      },
      middle: {
        title: 'Middle School (6-8)',
        subtitle: 'Academic Acceleration',
        desc: 'Middle school students engage with more complex texts, develop analytical writing skills, and begin exploring American literature.',
        features: ['Literature analysis and discussion', 'Essay writing and research skills', 'Critical thinking development'],
      },
      high: {
        title: 'High School (9-12)',
        subtitle: 'University Preparation',
        desc: 'High school students pursue advanced coursework preparing them for top universities worldwide, with the option to earn a US High School Diploma.',
        features: ['Advanced Literature and Composition', 'US History and Government', 'SAT/TOEFL Preparation', 'College counseling support'],
      },
      format: {
        title: 'Program Format',
        live: 'Live Sessions',
        liveDesc: '2 x 45-minute live classes per week with qualified teachers',
        async: 'Asynchronous Work',
        asyncDesc: 'Flexible homework and reading assignments',
        small: 'Small Groups',
        smallDesc: '4-8 students per class for personalized attention',
      },
    },
    // How It Works Page
    howItWorks: {
      heroTitle: 'How does it work?',
      heroSubtitle: 'A seamless parallel academic pathway designed to complement your child\'s French education.',
      step1: {
        title: 'Parallel to French School',
        desc: 'Our program runs alongside your child\'s regular French schooling, meeting in the late afternoon or early evening.',
      },
      step2: {
        title: 'Optimal Time Commitment',
        desc: 'Just 2x45 minutes of live classes per week, plus manageable homework assignments.',
      },
      step3: {
        title: 'Flexible & Supportive',
        desc: 'Asynchronous components allow students to work at their own pace when schedules get busy.',
      },
      step4: {
        title: 'Clear Academic Expectations',
        desc: 'Regular assessments, quarterly progress reports and semester report cards sent to parents through our own SIS (Student Information System). Constant communication to keep everyone aligned.',
      },
      profile: {
        title: 'OFA France Student Profile',
        point1: 'Curious, motivated students open to the world',
        point2: 'Students in the French school system seeking a bilingual and international pathway',
        point3: 'Evolving English level based on age, background and student goals',
        point4: 'Students capable of navigating multiple languages, cultures and ways of learning',
      },
    },
    // Difference Page
    difference: {
      heroTitle: 'The OFA France Difference',
      heroSubtitle: 'We\'re not a language school. We\'re not tutoring. We\'re a structured academic pathway.',
      vs: {
        title: 'How We Compare',
        ofa: 'OFA France',
        tutoring: 'Tutoring',
        intSchool: 'International School',
        langSchool: 'Language School',
        row1: 'Structured K-12 Curriculum',
        row2: 'Accredited Diploma Option',
        row3: 'Small Classes (4-8 students)',
        row4: 'Affordable Tuition',
        row5: 'Maintains French School Enrollment',
        row6: 'Academic Subject Instruction',
      },
      pillars: {
        title: 'Our Four Pillars',
        p1: 'Academic Rigor',
        p1Desc: 'Full American curriculum, not simplified language lessons.',
        p2: 'Long-Term Vision',
        p2Desc: 'A complete K-12 trajectory, not isolated interventions.',
        p3: 'Dual-System Expertise',
        p3Desc: 'Designed for students navigating French and American expectations.',
        p4: 'Community',
        p4Desc: 'A network of ambitious, internationally-minded families.',
      },
    },
    // Returning Families Page
    returning: {
      heroTitle: 'For Families Returning to France',
      heroSubtitle: 'Maintain your child\'s bilingual edge after living abroad.',
      challenge: {
        title: 'The Challenge of Returning',
        point1: 'Children lose English proficiency rapidly without continued exposure',
        point2: 'French school English classes are insufficient for maintaining native-level skills',
        point3: 'Social and academic reintegration can be challenging',
      },
      solution: {
        title: 'How OFA France Helps',
        point1: 'Continued academic instruction in English preserves language skills',
        point2: 'Connection with other bilingual students creates community',
        point3: 'Long-term pathway keeps international options open',
        point4: 'Flexible format adapts to busy reintegration schedules',
      },
    },
    // Diploma Page
    diploma: {
      heroTitle: 'US High School Diploma & International Pathways',
      heroSubtitle: 'Open doors to top universities in France, Europe, the UK, and North America.',
      cognia: {
        title: 'COGNIA Accredited',
        desc: 'Our US High School Diploma is accredited by COGNIA, the same accreditation held by leading American schools worldwide. This means your child\'s diploma is recognized by universities globally.',
      },
      pathways: {
        title: 'University Pathways',
        france: 'French Universities',
        franceDesc: 'Stand out in competitive programs with bilingual credentials.',
        europe: 'European Universities',
        europeDesc: 'Access English-taught programs across Europe.',
        uk: 'UK Universities',
        ukDesc: 'Strong preparation for Oxbridge and Russell Group applications.',
        us: 'North American Universities',
        usDesc: 'Direct pathway to American and Canadian universities.',
      },
    },
    // Admissions Page
    admissions: {
      heroTitle: 'Admissions',
      heroSubtitle: 'Begin your child\'s journey to bilingual academic excellence.',
      process: {
        title: 'Admission Process',
        step1: 'Initial Consultation',
        step1Desc: 'Schedule a discovery call to discuss your family\'s goals and learn about our programs.',
        step2: 'Assessment',
        step2Desc: 'We evaluate your child\'s English proficiency and academic readiness.',
        step3: 'Enrollment',
        step3Desc: 'Complete enrollment paperwork and select your program schedule.',
        step4: 'Orientation',
        step4Desc: 'Attend orientation and meet your child\'s teachers and classmates.',
      },
      timeline: {
        title: 'Enrollment Timeline',
        rolling: 'Rolling Admissions',
        rollingDesc: 'We accept applications year-round, with main entry points in September and January.',
        deadline: 'Priority Deadline',
        deadlineDesc: 'Apply by June 30 for guaranteed September placement.',
      },
      expectations: {
        title: 'What We Look For',
        point1: 'Intermediate to advanced English proficiency',
        point2: 'Academic motivation and curiosity',
        point3: 'Family commitment to the long-term program',
        point4: 'Current enrollment in French school system',
      },
    },
    // Tuition Page
    tuition: {
      heroTitle: 'Tuition & Fees',
      heroSubtitle: 'An accessible path to international education.',
      value: {
        title: 'The Value Proposition',
        desc: 'OFA France offers the academic rigor of international schooling at a fraction of the cost, while maintaining your child\'s place in the French system.',
      },
      comparison: {
        title: 'How We Compare',
        ofa: 'OFA France',
        ofaPrice: 'Starting at €2,400/year',
        intSchool: 'International School',
        intPrice: '€15,000-30,000/year',
        tutoring: 'Private Tutoring',
        tutoringPrice: '€3,000-8,000/year',
      },
      includes: {
        title: 'What\'s Included',
        point1: 'Live instruction with qualified teachers',
        point2: 'Full curriculum and materials',
        point3: 'Regular assessments and progress reports',
        point4: 'Parent communication and support',
        point5: 'Access to the OFA France community',
      },
      contact: 'Contact us for detailed pricing information',
    },
    // About Page
    about: {
      heroTitle: 'About OFA France',
      heroSubtitle: 'Building international academic futures from France.',
      mission: {
        title: 'Our Mission',
        desc: 'OFA France enables students in France to build truly international academic futures without leaving the French system. We believe every ambitious student deserves access to world-class bilingual education.',
      },
      vision: {
        title: 'Our Vision',
        desc: 'A Europe where families have accessible, high-quality options for international education that complement rather than replace local schooling.',
      },
      network: {
        title: 'Part of the OFA Lycée Network',
        desc: 'OFA France is part of the broader OFA Lycée ecosystem, connecting families across the United States, United Kingdom, and France who share a commitment to bilingual excellence.',
        link: 'Visit OFA Lycée USA',
      },
    },
    // Partnerships Page
    partnerships: {
      heroTitle: 'School Partnerships',
      heroSubtitle: 'Bring the OFA France program to your institution.',
      benefits: {
        title: 'Partner Benefits',
        point1: 'Offer bilingual education without hiring additional staff',
        point2: 'Differentiate your school in a competitive market',
        point3: 'Provide clear pathway to US High School Diploma',
        point4: 'Access to curriculum, training, and ongoing support',
      },
      ideal: {
        title: 'Ideal Partners',
        point1: 'Private K-8 schools seeking to enhance English offerings',
        point2: 'Bilingual schools looking for structured American curriculum',
        point3: 'International sections within French schools',
      },
      cta: 'Schedule a Partnership Discussion',
    },
    // Contact/Book Page
    contact: {
      heroTitle: 'Get in Touch',
      heroSubtitle: 'Ready to learn more? We\'re here to help.',
      form: {
        title: 'Request Information',
        firstName: 'First Name',
        lastName: 'Last Name',
        email: 'Email',
        phone: 'Phone',
        childGrade: 'Child\'s Current Grade',
        message: 'Message',
        submit: 'Send Request',
      },
      calendly: {
        title: 'Register',
        desc: 'Schedule a 30-minute call with our admissions team to discuss your family\'s goals.',
      },
    },
    // Common
    common: {
      learnMore: 'Learn More',
      getStarted: 'Get Started',
      viewPrograms: 'View Programs',
      scheduleCall: 'Schedule a Call',
      yes: 'Yes',
      no: 'No',
      partial: 'Partial',
    },
  },
  fr: {
    // Navigation
    nav: {
      programs: 'Programmes',
      howItWorks: 'Fonctionnement',
      difference: 'La Différence OFA',
      returningFamilies: 'Rester Bilingue',
      becomeBilingual: 'Devenir Bilingue',
      diploma: 'Diplôme & Parcours',
      admissions: 'Admissions',
      tuition: 'Tarifs',
      about: 'À Propos',
      partnerships: 'Partenariats',
      bookCall: 'S\'inscrire',
      requestInfo: 'Demander des Infos',
    },
    // Hero Section
    hero: {
      overline: '1ER LYCÉE FRANCO-AMÉRICAIN EN LIGNE',
      title: 'OFA France',
      titleHighlight: 'Une scolarité américaine.',
      titleHighlight2: 'Un avenir bilingue.',
      titleHighlight3: 'Pour tous.',
      subtitle: 'En parallèle de sa scolarité française, votre enfant suit un programme américain accrédité — de la Grande Section à la Terminale — et obtient un U.S. High School Diploma.',
      cta: 'S\'inscrire',
      secondaryCta: 'Explorer les Programmes',
      proof1: '4-8 Élèves',
      proof1Sub: 'par classe',
      proof2: 'COGNIA',
      proof2Sub: 'Accrédité',
      proof3: 'GS-Terminale',
      proof3Sub: 'Parcours américain sans interruption',
      proof4: '100% En Ligne',
      proof4Sub: 'Cours en direct depuis la maison',
    },
    // Problem Section
    problem: {
      overline: 'LE DÉFI',
      title: 'Comment donner une dimension internationale à la scolarité de votre enfant ?',
      subtitle: 'Dans un monde de plus en plus global, la maîtrise de l\'anglais et les accréditations académiques internationales sont essentielles pour réussir.',
      point1Title: 'Exposition Limitée à l\'Anglais',
      point1Desc: 'Les écoles françaises offrent peu d\'heures d\'anglais, insuffisantes pour un véritable bilinguisme académique.',
      point2Title: 'Les Enfants d\'Expatriés Perdent leur Niveau',
      point2Desc: 'Les enfants revenant de l\'étranger perdent souvent leur niveau d\'anglais en quelques mois.',
      point3Title: 'Les Écoles Internationales Sont Inaccessibles',
      point3Desc: 'Avec des listes d\'attente et des coûts élevés, les écoles internationales restent hors de portée.',
    },
    // Solution Section
    solution: {
      overline: 'LA SOLUTION',
      title: 'Un Parcours Académique Structuré en Anglais',
      subtitle: 'OFA France propose un curriculum américain complet en parallèle de l\'éducation française de votre enfant, développant une véritable compétence académique bilingue.',
      feature1: 'Pas une École de Langues',
      feature1Desc: 'Nous enseignons des matières académiques en anglais, pas seulement la langue.',
      feature2: 'Cohérence à Long Terme',
      feature2Desc: 'Un parcours structuré de la Grande Section à la Terminale, pas des cours isolés.',
      feature3: 'Expertise Double Système',
      feature3Desc: 'Conçu spécifiquement pour les élèves naviguant entre les attentes françaises et américaines.',
    },
    // Pathway Section
    pathway: {
      overline: 'LE PARCOURS ACADÉMIQUE',
      title: 'De la Grande Section au Diplôme de High School',
      subtitle: 'Une trajectoire éducative complète ouvrant vers les opportunités internationales.',
      k5Title: 'Élémentaire (GS-CM2)',
      k5Desc: 'Construire les bases bilingues précoces grâce à un enseignement engageant en anglais.',
      middleTitle: 'Collège',
      middleDesc: 'Anglais académique structuré avec écriture, littérature et pensée critique.',
      highTitle: 'Lycée',
      highDesc: 'Cours avancés avec préparation optionnelle au diplôme de High School américain.',
    },
    // Outcomes Section
    outcomes: {
      overline: 'OPPORTUNITÉS FUTURES',
      title: 'Ouvrir des Portes dans le Monde Entier',
      subtitle: 'Les diplômés OFA France sont préparés pour les meilleures universités en France, en Europe, au Royaume-Uni et en Amérique du Nord.',
      stat1: '100%',
      stat1Label: 'Acceptation Universitaire',
      stat2: '15+',
      stat2Label: 'Pays Représentés',
      stat3: '90%',
      stat3Label: 'Bénéfices Professionnels',
    },
    // Proof Section
    proof: {
      overline: 'POURQUOI NOUS CHOISIR',
      title: 'L\'Avantage OFA France',
      small: 'Petites Classes',
      smallDesc: '4-8 élèves maximum assure une attention personnalisée et une participation significative.',
      teachers: 'Enseignants Experts',
      teachersDesc: 'Éducateurs anglophones natifs avec expérience des curricula internationaux.',
      accredited: 'Accrédité COGNIA',
      accreditedDesc: 'Notre diplôme de High School américain est reconnu par les universités du monde entier.',
      dual: 'Conçu Double Système',
      dualDesc: 'Construit spécifiquement pour les élèves équilibrant les académiques français et américains.',
    },
    // Objection Section
    objection: {
      overline: 'QUESTIONS FRÉQUENTES',
      title: 'Comprendre les Frais',
      q1: 'Quelle différence avec le soutien scolaire ?',
      a1: 'OFA France est un programme académique structuré, pas du soutien scolaire. Nous suivons un curriculum américain complet avec des enseignants qualifiés, des évaluations régulières et une trajectoire éducative à long terme.',
      q2: 'Comment se compare-t-il aux écoles internationales ?',
      a2: 'Nous offrons la rigueur académique des écoles internationales à une fraction du coût, avec la flexibilité de maintenir la scolarité française et les liens sociaux de votre enfant.',
      q3: 'Mon enfant sera-t-il surchargé ?',
      a3: 'Notre programme est conçu pour compléter l\'école française avec seulement 2x45 minutes par semaine, plus du travail asynchrone flexible. La plupart des élèves trouvent cela enrichissant, pas accablant.',
    },
    // CTA Section
    cta: {
      title: 'Prêt à Construire l\'Avenir International de Votre Enfant ?',
      subtitle: 'Planifiez un appel découverte gratuit avec notre équipe d\'admissions pour discuter des objectifs de votre famille.',
      primary: 'S\'inscrire',
      secondary: 'Demander des Informations',
    },
    // Footer
    footer: {
      tagline: 'Construire des avenirs académiques internationaux depuis la France.',
      programs: 'Programmes',
      about: 'À Propos',
      contact: 'Contact',
      legal: 'Légal',
      privacy: 'Politique de Confidentialité',
      terms: 'Conditions d\'Utilisation',
      copyright: '© 2025 OFA France. Tous droits réservés.',
      partOf: 'Partie du réseau OFA Lycée.',
    },
    // Programs Page
    programs: {
      heroTitle: 'Programmes Académiques',
      heroSubtitle: 'Un parcours complet de la Grande Section à la Terminale conçu pour les élèves en France recherchant une véritable éducation bilingue.',
      k5: {
        title: 'Élémentaire (GS-CM2)',
        subtitle: 'Construire les Fondations',
        desc: 'Notre programme élémentaire établit de solides bases en anglais grâce à un enseignement engageant et adapté à l\'âge qui développe la lecture, l\'écriture et l\'expression orale.',
        features: ['Lecture et narration interactives', 'Ateliers d\'écriture créative', 'Enrichissement du vocabulaire par le jeu', 'Activités d\'exploration culturelle'],
      },
      middle: {
        title: 'Collège (6e-4e)',
        subtitle: 'Accélération Académique',
        desc: 'Les collégiens travaillent sur des textes plus complexes, développent des compétences en rédaction analytique et commencent à explorer la littérature américaine.',
        features: ['Analyse et discussion littéraire', 'Rédaction et recherche', 'Développement de la pensée critique'],
      },
      high: {
        title: 'Lycée (3e-Terminale)',
        subtitle: 'Préparation Universitaire',
        desc: 'Les lycéens suivent des cours avancés les préparant aux meilleures universités du monde, avec la possibilité d\'obtenir un diplôme de High School américain.',
        features: ['Littérature et Composition avancées', 'Histoire et Gouvernement des États-Unis', 'Préparation SAT/TOEFL', 'Accompagnement orientation universitaire'],
      },
      format: {
        title: 'Format du Programme',
        live: 'Sessions en Direct',
        liveDesc: '2 x 45 minutes de cours en direct par semaine avec des enseignants qualifiés',
        async: 'Travail Asynchrone',
        asyncDesc: 'Devoirs et lectures flexibles',
        small: 'Petits Groupes',
        smallDesc: '4-8 élèves par classe pour une attention personnalisée',
      },
    },
    // How It Works Page
    howItWorks: {
      heroTitle: 'Comment ça fonctionne ?',
      heroSubtitle: 'Un parcours académique parallèle fluide conçu pour compléter l\'éducation française de votre enfant.',
      step1: {
        title: 'Parallèle à l\'École Française',
        desc: 'Notre programme fonctionne en parallèle de la scolarité française régulière, avec des cours en fin d\'après-midi ou début de soirée.',
      },
      step2: {
        title: 'Engagement Optimal en Temps',
        desc: 'Seulement 2x45 minutes de cours en direct par semaine, plus des devoirs gérables.',
      },
      step3: {
        title: 'Flexible & Accompagnant',
        desc: 'Les composantes asynchrones permettent aux élèves de travailler à leur rythme quand les emplois du temps sont chargés.',
      },
      step4: {
        title: 'Attentes Académiques Claires',
        desc: 'Évaluations régulières, bulletins de progression par quarter (progress reports) et par semestre envoyés aux parents à travers notre propre SIS (Student Information System). Communication constante pour maintenir tout le monde aligné.',
      },
      profile: {
        title: 'Profil d\'Élève OFA France',
        point1: 'Élèves curieux, motivés et ouverts sur le monde',
        point2: 'Élèves suivant une scolarité française et souhaitant développer un parcours bilingue et international',
        point3: 'Niveau d\'anglais évolutif selon l\'âge, le parcours et les objectifs de l\'élève',
        point4: 'Élèves capables d\'évoluer entre plusieurs langues, cultures et façons d\'apprendre',
      },
    },
    // Difference Page
    difference: {
      heroTitle: 'La Différence OFA France',
      heroSubtitle: 'Nous ne sommes pas une école de langues. Nous ne faisons pas de soutien scolaire. Nous sommes un parcours académique structuré.',
      vs: {
        title: 'Comparaison',
        ofa: 'OFA France',
        tutoring: 'Soutien Scolaire',
        intSchool: 'École Internationale',
        langSchool: 'École de Langues',
        row1: 'Curriculum K-12 Structuré',
        row2: 'Option Diplôme Accrédité',
        row3: 'Petites Classes (4-8 élèves)',
        row4: 'Tarifs Accessibles',
        row5: 'Maintien de l\'École Française',
        row6: 'Enseignement de Matières Académiques',
      },
      pillars: {
        title: 'Nos Quatre Piliers',
        p1: 'Rigueur Académique',
        p1Desc: 'Curriculum américain complet, pas des cours de langue simplifiés.',
        p2: 'Vision à Long Terme',
        p2Desc: 'Une trajectoire complète de la Grande Section à la Terminale, pas des interventions isolées.',
        p3: 'Expertise Double Système',
        p3Desc: 'Conçu pour les élèves naviguant entre les attentes françaises et américaines.',
        p4: 'Communauté',
        p4Desc: 'Un réseau de familles ambitieuses à l\'esprit international.',
      },
    },
    // Returning Families Page
    returning: {
      heroTitle: 'Pour les Familles de Retour en France',
      heroSubtitle: 'Maintenez l\'avantage bilingue de votre enfant après avoir vécu à l\'étranger.',
      challenge: {
        title: 'Le Défi du Retour',
        point1: 'Les enfants perdent rapidement leur niveau d\'anglais sans exposition continue',
        point2: 'Les cours d\'anglais de l\'école française sont insuffisants pour maintenir un niveau natif',
        point3: 'La réintégration sociale et académique peut être difficile',
      },
      solution: {
        title: 'Comment OFA France Aide',
        point1: 'L\'enseignement académique continu en anglais préserve les compétences linguistiques',
        point2: 'La connexion avec d\'autres élèves bilingues crée une communauté',
        point3: 'Le parcours à long terme garde les options internationales ouvertes',
        point4: 'Le format flexible s\'adapte aux emplois du temps chargés de réintégration',
      },
    },
    // Diploma Page
    diploma: {
      heroTitle: 'Diplôme de High School US & Parcours Internationaux',
      heroSubtitle: 'Ouvrez les portes des meilleures universités en France, en Europe, au Royaume-Uni et en Amérique du Nord.',
      cognia: {
        title: 'Accrédité COGNIA',
        desc: 'Notre diplôme de High School américain est accrédité par COGNIA, la même accréditation détenue par les grandes écoles américaines dans le monde. Cela signifie que le diplôme de votre enfant est reconnu par les universités du monde entier.',
      },
      pathways: {
        title: 'Parcours Universitaires',
        france: 'Universités Françaises',
        franceDesc: 'Démarquez-vous dans les programmes compétitifs avec des accréditations bilingues.',
        europe: 'Universités Européennes',
        europeDesc: 'Accédez aux programmes enseignés en anglais à travers l\'Europe.',
        uk: 'Universités Britanniques',
        ukDesc: 'Préparation solide pour Oxbridge et les candidatures Russell Group.',
        us: 'Universités Nord-Américaines',
        usDesc: 'Accès direct aux universités américaines et canadiennes.',
      },
    },
    // Admissions Page
    admissions: {
      heroTitle: 'Admissions',
      heroSubtitle: 'Commencez le parcours de votre enfant vers l\'excellence académique bilingue.',
      process: {
        title: 'Processus d\'Admission',
        step1: 'Consultation Initiale',
        step1Desc: 'Planifiez un appel découverte pour discuter des objectifs de votre famille et découvrir nos programmes.',
        step2: 'Évaluation',
        step2Desc: 'Nous évaluons le niveau d\'anglais et la préparation académique de votre enfant.',
        step3: 'Inscription',
        step3Desc: 'Complétez les documents d\'inscription et sélectionnez l\'emploi du temps de votre programme.',
        step4: 'Orientation',
        step4Desc: 'Participez à l\'orientation et rencontrez les enseignants et camarades de votre enfant.',
      },
      timeline: {
        title: 'Calendrier d\'Inscription',
        rolling: 'Admissions Continues',
        rollingDesc: 'Nous acceptons les candidatures toute l\'année, avec des entrées principales en septembre et janvier.',
        deadline: 'Date Limite Prioritaire',
        deadlineDesc: 'Postulez avant le 30 juin pour un placement garanti en septembre.',
      },
      expectations: {
        title: 'Ce Que Nous Recherchons',
        point1: 'Maîtrise de l\'anglais intermédiaire à avancée',
        point2: 'Motivation académique et curiosité',
        point3: 'Engagement familial dans le programme à long terme',
        point4: 'Scolarisation actuelle dans le système français',
      },
    },
    // Tuition Page
    tuition: {
      heroTitle: 'Frais de Scolarité',
      heroSubtitle: 'Un chemin accessible vers l\'éducation internationale.',
      value: {
        title: 'La Proposition de Valeur',
        desc: 'OFA France offre la rigueur académique des écoles internationales à une fraction du coût, tout en maintenant la place de votre enfant dans le système français.',
      },
      comparison: {
        title: 'Comparaison',
        ofa: 'OFA France',
        ofaPrice: 'À partir de 2 400€/an',
        intSchool: 'École Internationale',
        intPrice: '15 000-30 000€/an',
        tutoring: 'Soutien Scolaire Privé',
        tutoringPrice: '3 000-8 000€/an',
      },
      includes: {
        title: 'Ce Qui Est Inclus',
        point1: 'Enseignement en direct avec des enseignants qualifiés',
        point2: 'Curriculum et matériel complets',
        point3: 'Évaluations régulières et bulletins de progression',
        point4: 'Communication et soutien aux parents',
        point5: 'Accès à la communauté OFA France',
      },
      contact: 'Contactez-nous pour des informations tarifaires détaillées',
    },
    // About Page
    about: {
      heroTitle: 'À Propos d\'OFA France',
      heroSubtitle: 'Construire des avenirs académiques internationaux depuis la France.',
      mission: {
        title: 'Notre Mission',
        desc: 'OFA France permet aux élèves en France de construire de véritables avenirs académiques internationaux sans quitter le système français. Nous croyons que chaque élève ambitieux mérite l\'accès à une éducation bilingue de classe mondiale.',
      },
      vision: {
        title: 'Notre Vision',
        desc: 'Une Europe où les familles ont des options accessibles et de haute qualité pour l\'éducation internationale qui complètent plutôt que remplacent la scolarité locale.',
      },
      network: {
        title: 'Partie du Réseau OFA Lycée',
        desc: 'OFA France fait partie de l\'écosystème plus large OFA Lycée, connectant les familles aux États-Unis, au Royaume-Uni et en France qui partagent un engagement envers l\'excellence bilingue.',
        link: 'Visiter OFA Lycée USA',
      },
    },
    // Partnerships Page
    partnerships: {
      heroTitle: 'Partenariats Scolaires',
      heroSubtitle: 'Apportez le programme OFA France à votre établissement.',
      benefits: {
        title: 'Avantages Partenaires',
        point1: 'Offrez une éducation bilingue sans recruter de personnel supplémentaire',
        point2: 'Différenciez votre école sur un marché compétitif',
        point3: 'Proposez un parcours clair vers le diplôme de High School US',
        point4: 'Accès au curriculum, à la formation et au soutien continu',
      },
      ideal: {
        title: 'Partenaires Idéaux',
        point1: 'Écoles privées de la maternelle au collège cherchant à améliorer l\'offre anglaise',
        point2: 'Écoles bilingues recherchant un curriculum américain structuré',
        point3: 'Sections internationales au sein des écoles françaises',
      },
      cta: 'Planifier une Discussion Partenariat',
    },
    // Contact/Book Page
    contact: {
      heroTitle: 'Contactez-Nous',
      heroSubtitle: 'Prêt à en savoir plus ? Nous sommes là pour vous aider.',
      form: {
        title: 'Demander des Informations',
        firstName: 'Prénom',
        lastName: 'Nom',
        email: 'Email',
        phone: 'Téléphone',
        childGrade: 'Classe Actuelle de l\'Enfant',
        message: 'Message',
        submit: 'Envoyer la Demande',
      },
      calendly: {
        title: 'S\'inscrire',
        desc: 'Planifiez un appel de 30 minutes avec notre équipe d\'admissions pour discuter des objectifs de votre famille.',
      },
    },
    // Common
    common: {
      learnMore: 'En Savoir Plus',
      getStarted: 'Commencer',
      viewPrograms: 'Voir les Programmes',
      scheduleCall: 'Planifier un Appel',
      yes: 'Oui',
      no: 'Non',
      partial: 'Partiel',
    },
  },
};

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('ofa-language');
      if (saved) return saved;
      const browserLang = navigator.language.split('-')[0];
      return browserLang === 'fr' ? 'fr' : 'en';
    }
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem('ofa-language', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = translations[language];
  const toggleLanguage = () => setLanguage(l => l === 'en' ? 'fr' : 'en');

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};
